import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { isValidEmail, parseEmailsFromPaste } from '../../utils/enterpriseUi';
import '../../styles/EnterpriseAdminOnboarding.css';

const STEPS = 4;

/** @deprecated Local storage is not authoritative; kept for older imports/tests. */
export function teamsSetupStorageKey(enterpriseId) {
  return `velox:teams-setup:${enterpriseId}`;
}

export default function TeamsSetupWizard({ enterpriseId, companyName, onFinished }) {
  const { showError, showSuccess } = useToast();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [emailsText, setEmailsText] = useState('');
  const [members, setMembers] = useState([]);
  const [proIds, setProIds] = useState([]);

  const loadMembers = useCallback(async () => {
    try {
      const res = await api.get('/api/enterprise/members', { params: { enterpriseId } });
      setMembers(res.data.members || []);
    } catch {
      setMembers([]);
    }
  }, [enterpriseId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const patchSetup = async (action) => {
    await api.patch('/api/enterprise/workspace-setup', { enterpriseId, action });
  };

  const finish = async (action) => {
    setSubmitting(true);
    try {
      await patchSetup(action);
      onFinished?.();
    } catch (err) {
      showError(err.response?.data?.message || err.response?.data?.error || 'Could not save setup');
    } finally {
      setSubmitting(false);
    }
  };

  const sendInvites = async () => {
    const emails = [...new Set(parseEmailsFromPaste(emailsText).filter(isValidEmail))];
    if (emails.length === 0) return true;
    setSubmitting(true);
    try {
      for (const email of emails) {
        await api.post('/api/enterprise/invite', { enterpriseId, email });
      }
      showSuccess(emails.length === 1 ? 'Invite sent.' : `${emails.length} invites sent.`);
      await loadMembers();
      return true;
    } catch (err) {
      showError(err.response?.data?.message || err.response?.data?.error || 'Could not send invites');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const assignPro = async () => {
    if (proIds.length === 0) return true;
    setSubmitting(true);
    try {
      for (const userId of proIds) {
        await api.post('/api/enterprise/licences/assign', { userId }, { params: { enterpriseId } });
      }
      showSuccess('Pro licence assigned.');
      await loadMembers();
      return true;
    } catch (err) {
      showError(err.response?.data?.message || err.response?.data?.error || 'Could not assign Pro');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const next = async () => {
    if (step === 1) {
      const ok = await sendInvites();
      if (!ok) return;
    }
    if (step === 2) {
      const ok = await assignPro();
      if (!ok) return;
    }
    if (step === STEPS - 1) {
      await finish('complete');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS - 1));
  };

  const name = companyName || 'Your company';
  const inviteCount = parseEmailsFromPaste(emailsText).filter(isValidEmail).length;
  const proPeople = members.filter((m) => proIds.includes(String(m.userId)));

  let content = null;
  if (step === 0) {
    content = (
      <>
        <p className="ent-onb-eyebrow">Teams</p>
        <h2 className="ent-onb-title">Set up {name}</h2>
        <p className="ent-onb-lead">
          Teams includes member capacity and one Pro licence. You can invite people and choose who gets Pro now, or skip and do it later from Seats.
        </p>
        <div className="ent-form-group">
          <label htmlFor="teams-company-name">Company name</label>
          <input id="teams-company-name" className="ent-input" value={name} readOnly />
        </div>
      </>
    );
  } else if (step === 1) {
    content = (
      <>
        <p className="ent-onb-eyebrow">People</p>
        <h2 className="ent-onb-title">Invite colleagues</h2>
        <p className="ent-onb-lead">One email per line. They join as Members on Teams Basic.</p>
        <div className="ent-form-group">
          <label htmlFor="teams-invite-emails">Emails</label>
          <textarea
            id="teams-invite-emails"
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            placeholder="alex@company.com"
          />
        </div>
      </>
    );
  } else if (step === 2) {
    content = (
      <>
        <p className="ent-onb-eyebrow">Pro licences</p>
        <h2 className="ent-onb-title">Who gets Pro?</h2>
        <p className="ent-onb-lead">
          Teams includes Pro for Insights. Choose who should receive an included Pro licence.
        </p>
        {members.length === 0 ? (
          <p className="ent-onb-lead">No members yet. Invite people, then assign Pro from Seats.</p>
        ) : (
          <div className="ent-check-list">
            {members.map((m) => (
              <label key={m.userId}>
                <input
                  type="checkbox"
                  checked={proIds.includes(String(m.userId))}
                  onChange={() => {
                    const id = String(m.userId);
                    setProIds((current) => (
                      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
                    ));
                  }}
                />
                <span>{m.name || m.email}</span>
              </label>
            ))}
          </div>
        )}
      </>
    );
  } else {
    content = (
      <>
        <p className="ent-onb-eyebrow">Ready</p>
        <h2 className="ent-onb-title">You&apos;re set</h2>
        <ul className="ent-onb-list">
          <li><strong>Company:</strong> {name}</li>
          <li><strong>Invites:</strong> {inviteCount || 'None this time'}</li>
          <li>
            <strong>Pro:</strong>{' '}
            {proPeople.length ? proPeople.map((m) => m.name || m.email).join(', ') : 'None assigned yet'}
          </li>
        </ul>
      </>
    );
  }

  return (
    <div className="ent-onb-overlay" role="dialog" aria-modal="true" aria-label="Teams setup">
      <div className="ent-onb-panel">
        <div className="ent-onb-progress" aria-hidden="true">
          {Array.from({ length: STEPS }, (_, i) => (
            <span key={i} className={`ent-onb-dot${i === step ? ' active' : ''}`} />
          ))}
        </div>
        <div className="ent-onb-body">{content}</div>
        <div className="ent-onb-footer">
          {step > 0 ? (
            <button type="button" className="ent-onb-btn ent-onb-btn-ghost" onClick={() => setStep((s) => s - 1)} disabled={submitting}>
              Back
            </button>
          ) : (
            <span />
          )}
          <div className="ent-onb-footer-spacer" />
          <button
            type="button"
            className="ent-onb-btn ent-onb-btn-secondary"
            onClick={() => finish('skip')}
            disabled={submitting}
          >
            Skip for now
          </button>
          <button type="button" className="ent-onb-btn ent-onb-btn-primary" onClick={next} disabled={submitting}>
            {submitting ? 'Saving…' : step === STEPS - 1 ? 'Finish' : 'Continue'}
          </button>
          <p className="ent-onb-hint">You can finish invites and Pro licences from the sidebar anytime.</p>
        </div>
      </div>
    </div>
  );
}
